import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { QuoteService } from 'src/app/admin-core/apiservices/quote.service';
import { AlertService } from 'src/app/admin-core/services';
import { Constants } from 'src/app/admin-core/constants';
import { RequestParamModel } from '@coremodels';

@Component({
  standalone: false,
  selector: 'app-date-history-modal',
  // Eager: this Angular version defaults components to OnPush. The history rows
  // are fetched async and the loader is cleared in the subscribe callback, but an
  // OnPush view never repainted — leaving an endless spinner and an empty table.
  // Eager keeps it painting. Same fix as ConfirmModalComponent.
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './date-history-modal.component.html',
  styleUrls: ['./date-history-modal.component.css']
})
export class DateHistoryModalComponent implements OnInit {
  @Input() title: string = '';
  @Input() type: string = 'SRD';   // 'SRD' | 'SCD' | 'MSF'
  @Input() level: string = 'quote'; // 'quote' | 'product'
  @Input() entityId: number;
  /** Rows the caller already holds, in this table's shape (last_date, new_date, updated_date,
   *  updated_by, notes). When given, they are shown as they are and nothing is fetched. */
  @Input() rows: any[] | null = null;

  constants = Constants;
  // GC-339: manual storage-fee history shows amount columns instead of date columns.
  get isManualFee(): boolean {
    return this.type === Constants.MSF_HISTORY_TYPE;
  }

  isLoading = false;
  isLoadingMore = false;
  historyList: any[] = [];
  hasMore = true;
  requestParamModel: RequestParamModel;

  constructor(
    public activeModal: NgbActiveModal,
    private quoteService: QuoteService,
    private alertService: AlertService
  ) {
    this.requestParamModel = new RequestParamModel();
    this.requestParamModel.pageSize = 100; // temporary set a pageSize
    this.requestParamModel.pageIndex = 0;
  }

  ngOnInit(): void {
    if (this.rows) {
      this.historyList = this.rows;
      this.hasMore = false;
      return;
    }
    this.loadHistory(true);
  }

  loadHistory(reset = false): void {
    if (reset) {
      this.requestParamModel.pageIndex = 0;
      this.historyList = [];
      this.hasMore = true;
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    const obs = this.isManualFee
      ? this.quoteService.getManualStorageFeeHistory(this.entityId, this.requestParamModel)
      : this.level === 'quote'
        ? this.quoteService.getDateHistory(this.entityId, this.type, this.requestParamModel)
        : this.quoteService.getProductDateHistory(this.entityId, this.type, this.requestParamModel);

    obs.subscribe({
      next: (result) => {
        this.requestParamModel.update(result);
        this.historyList = [...this.historyList, ...(result.data || [])];
        this.hasMore = this.historyList.length < this.requestParamModel.totalCount;
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error) => {
        this.alertService.error(error);
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 50 && this.hasMore && !this.isLoading && !this.isLoadingMore) {
      this.requestParamModel.pageIndex++;
      this.loadHistory(false);
    }
  }

  close(): void {
    this.activeModal.close();
  }

  formatDate(date: string): string {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }
}
